/**
 * Tiny pure request-generation/ownership tracker for stale-async-response
 * safety in components with multiple concurrent/superseding async
 * operations (e.g. ProjectDocumentsAssistantPage.js).
 *
 * Deliberately NOT React-specific -- no hooks, no JSX, no dependency on
 * component state. This makes it directly unit-testable with a plain Node
 * `assert` script (see tests/utils/request-generation.test.js, which loads
 * this ES module via dynamic import()), independent of whether a React
 * test runner is available.
 *
 * Each named "channel" (e.g. "conversations", "messages") owns its own
 * monotonically increasing generation counter:
 *
 *   - begin(channel): bumps the channel's counter and returns the new
 *     value. Callers capture this value synchronously, at the exact moment
 *     an async operation starts.
 *   - isCurrent(channel, generation): true only if no NEWER begin()/
 *     invalidate() call has happened on that channel since. An async
 *     operation's success/error/finally callback must check this before
 *     applying any state -- if false, the callback's result is stale and
 *     must be dropped.
 *   - invalidate(channel): bumps the counter WITHOUT an associated new
 *     request, superseding any in-flight request on that channel without
 *     starting a replacement (e.g. the user navigated away before a
 *     replacement request was ready to start).
 *   - invalidateAll(): invalidates every channel at once (e.g. a
 *     higher-level scope transition, such as switching the globally
 *     selected project, which can affect several channels simultaneously).
 *
 * Because ownership is a per-channel monotonic counter rather than a
 * comparison against some other "current scope" value, this correctly
 * handles BOTH cross-scope invalidation (a project/discipline/conversation
 * switch supersedes older requests) AND same-scope supersession (two
 * requests to the exact same scope -- the later one always wins) with one
 * mechanism, and never depends on some other ref/state having already been
 * updated by the time a request resolves.
 */
export function createRequestGenerationTracker(channels) {
  if (!Array.isArray(channels) || channels.length === 0) {
    throw new Error(
      'createRequestGenerationTracker requires a nonempty channel list'
    );
  }

  const generations = {};
  channels.forEach((channel) => {
    generations[channel] = 0;
  });

  const assertKnownChannel = (channel) => {
    if (!Object.prototype.hasOwnProperty.call(generations, channel)) {
      throw new Error(`Unknown request-generation channel: ${channel}`);
    }
  };

  const begin = (channel) => {
    assertKnownChannel(channel);
    generations[channel] += 1;
    return generations[channel];
  };

  const invalidate = (channel) => {
    assertKnownChannel(channel);
    generations[channel] += 1;
  };

  const invalidateAll = () => {
    channels.forEach((channel) => {
      generations[channel] += 1;
    });
  };

  const isCurrent = (channel, generation) => {
    assertKnownChannel(channel);
    return generations[channel] === generation;
  };

  return { begin, invalidate, invalidateAll, isCurrent };
}
